import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, CheckCircle2, LogOut, Users, Settings2, CalendarCheck, Clock } from "lucide-react";
import { isAdmin, isApprover, homeFor, ROLE_LABELS } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ForcedPasswordDialog } from "@/components/ForcedPasswordDialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const nav = isAdmin(user)
    ? [
        { to: "/approvals", label: "Approvals", icon: CheckCircle2 },
        { to: "/hours", label: "Monthly Hours", icon: Clock },
        { to: "/team", label: "Team", icon: Users },
        { to: "/admin", label: "Admin", icon: Settings2 },
      ]
    : isApprover(user)
      ? [{ to: "/approvals", label: "Approvals", icon: CheckCircle2 }]
      : [
          { to: "/", label: "My Timesheets", icon: LayoutDashboard },
          { to: "/month", label: "Month end", icon: CalendarCheck },
        ];

  const initials = (user?.name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <header
        data-testid="app-header"
        className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
          <Link to={homeFor(user)} className="flex items-center gap-2.5">
            <img src="/mokora-navy.png" alt="Mokora" className="h-7 w-auto dark:hidden" />
            <img src="/mokora-white.png" alt="Mokora" className="hidden h-7 w-auto dark:block" />
          </Link>

          <nav className="ml-4 hidden items-center gap-1 sm:flex">
            {nav.map((item) => {
              const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    active
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <ThemeToggle />
            <ChangePasswordDialog />
            <div className="hidden items-center gap-2.5 sm:flex">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-secondary text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="leading-tight">
                <div className="text-sm font-semibold">{user?.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  {ROLE_LABELS[user?.role] || user?.role}
                </div>
              </div>
            </div>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="icon"
              onClick={onLogout}
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <nav data-testid="mobile-nav" className="flex gap-1 overflow-x-auto border-t border-border px-4 py-2 sm:hidden">
          {nav.map((item) => {
            const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
            return (
              <Link key={item.to} to={item.to} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={cn("flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium",
                  active ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground")}>
                <item.icon className="h-4 w-4" /> {item.label}
              </Link>
            );
          })}
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>
      {user?.must_change_password && <ForcedPasswordDialog />}
    </div>
  );
}
